import { MoreHorizRounded } from "@mui/icons-material";
import {
  Box,
  Stack,
  Checkbox,
  Avatar,
  Typography,
  Chip,
  Button,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useState } from "react";
import type { IComment } from "../../../../../types/comment";
import apiService from "../../../../../service/Api";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../../components/common/ShowToast";
import { handleExport } from "../utils/handleCommentExport";
import { AddContactModal } from "./AddContactModal";
import { EditCommentModal } from "./EditCommentModal";

interface CommentDesktopCardProps {
  comment: IComment;
  checked: boolean;
  onCheck: () => void;
  isLast?: boolean;
  refreshComments: (comment: IComment, isDeleting: boolean) => void;
  fetchApprovedComments: () => Promise<void>;
  existingContactEmails: string[];
  sourceLabel?: string;
}

export const CommentDesktopCard: React.FC<CommentDesktopCardProps> = ({
  comment,
  checked,
  onCheck,
  isLast = false,
  refreshComments,
  fetchApprovedComments,
  existingContactEmails,
  sourceLabel = "Memorial Page",
}) => {
  const [expanded, setExpanded] = useState(false);
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [addContactModalOpen, setAddContactModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchorEl);
  const closeMenu = () => setMenuAnchorEl(null);

  const TRUNCATE_LENGTH = 420;
  const isLong = comment.content.length > TRUNCATE_LENGTH;
  const displayContent =
    !expanded && isLong
      ? comment.content.slice(0, TRUNCATE_LENGTH) + "..."
      : comment.content;

  const isYoutube = sourceLabel.toLowerCase() === "youtube";
  const sourceColor = isYoutube ? "#ef4444" : "#724e41";
  const sourceBg = isYoutube ? "#fef2f2" : "#fdf2ee";

  const AVATAR_COLORS = [
    "#1abc9c",
    "#3498db",
    "#9b59b6",
    "#e67e22",
    "#e74c3c",
    "#2ecc71",
    "#f39c12",
    "#16a085",
    "#8e44ad",
    "#2980b9",
    "#d35400",
    "#c0392b",
  ];

  const displayName = (comment.author ?? "A").replace(/^@/, "").trim() || "A";

  const avatarColor =
    AVATAR_COLORS[
      Math.abs(
        displayName
          .split("")
          .reduce((hash, char) => (hash << 5) - hash + char.charCodeAt(0), 0),
      ) % AVATAR_COLORS.length
    ];

  const initials = displayName
    .split(" ")
    .map((n) => n.charAt(0))
    .slice(0, 1)
    .join("")
    .toUpperCase();

  const isConverted = existingContactEmails.includes(
    comment.authorEmail?.toLowerCase() ?? "",
  );

  const isBusy = isStatusUpdating || isDeleting;

  const deleteComment = async () => {
    setIsDeleting(true);
    try {
      await apiService.delete(`/api/chats/memorial-message/${comment.id}/`);
      refreshComments(comment, true);
      showSuccessToast("Comment deleted");
    } catch {
      showErrorToast("Failed to delete the comment, please try again");
    } finally {
      setIsDeleting(false);
    }
  };

  const updateApproveStatus = async () => {
    setIsStatusUpdating(true);
    try {
      await apiService.patch(`/api/chats/memorial-message/${comment.id}/`, {
        is_approved: !comment.isApproved,
      });
      const updatedComment = { ...comment, isApproved: !comment.isApproved };
      refreshComments(updatedComment, false);
      showSuccessToast(
        `Comment ${updatedComment.isApproved ? "Approved" : "Unapproved"}`,
      );
      fetchApprovedComments();
    } catch (error) {
      console.error(error);
      showErrorToast(
        `Failed to ${comment.isApproved ? "unapprove" : "approve"} comment.`,
      );
    } finally {
      setIsStatusUpdating(false);
    }
  };

  return (
    <Box sx={{ py: 1.5, position: "relative" }}>
      {addContactModalOpen && (
        <AddContactModal
          comment={comment}
          setAddContactModalOpen={setAddContactModalOpen}
        />
      )}

      {editModalOpen && (
        <EditCommentModal
          comment={comment}
          open={editModalOpen}
          setOpen={setEditModalOpen}
          refreshComments={refreshComments}
        />
      )}

      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Checkbox
          checked={checked}
          onChange={onCheck}
          size="medium"
          sx={{
            p: 0,
            mt: 0.5,
            color: "text.secondary",
            "&.Mui-checked": { color: "#724e41" },
          }}
        />

        {!isLast && (
          <Box
            sx={{
              position: "absolute",
              left: 38,
              top: 56,
              bottom: -4,
              width: "2px",
              bgcolor: "action.hover",
            }}
          />
        )}

        <Avatar
          sx={{
            width: 36,
            height: 36,
            bgcolor: avatarColor,
            fontSize: "13px",
            fontWeight: 700,
            zIndex: 1,
          }}
        >
          {initials}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "flex-start" }}
            gap={0.5}
          >
            <Box>
              <Typography
                sx={{ fontWeight: 700, fontSize: "14px", color: "#1e293b" }}
              >
                {comment.author}
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                mt={0.3}
                flexWrap="wrap"
              >
                <Chip
                  label={sourceLabel}
                  size="small"
                  sx={{
                    bgcolor: sourceBg,
                    color: sourceColor,
                    fontWeight: 600,
                    fontSize: "11px",
                    height: 20,
                    borderRadius: "4px",
                  }}
                />
                <Typography sx={{ fontSize: "11px", color: "#1e293b" }}>
                  {comment.timestamp}
                </Typography>
                {comment.authorEmail && comment.authorEmail !== "Anonymous" && (
                  <Typography sx={{ fontSize: "11px", color: "text.secondary" }}>
                    {comment.authorEmail}
                  </Typography>
                )}
              </Stack>
            </Box>
            <Stack alignItems="flex-end" spacing={0.3}>
              <Chip
                label={comment.isApproved ? "Approved" : "Pending"}
                size="small"
                sx={{
                  bgcolor: comment.isApproved ? "#4BA663" : "#F79E1B40",
                  color: comment.isApproved ? "#fff" : "inherit",
                  fontWeight: 600,
                  fontSize: "11px",
                  height: 22,
                }}
              />
              {comment.isApproved && comment.approvedByUsername && (
                <Typography
                  sx={{ fontSize: "10px", color: "text.secondary" }}
                >
                  by {comment.approvedByUsername}
                </Typography>
              )}
            </Stack>
          </Stack>

          {/* Body */}
          <Typography
            sx={{
              fontSize: "13px",
              color: "text.primary",
              lineHeight: 1.6,
              mt: 1.5,
              mb: 1.5,
            }}
          >
            {displayContent}
            {isLong && (
              <Box
                component="span"
                onClick={() => setExpanded(!expanded)}
                sx={{
                  color: "#724e41",
                  fontWeight: 600,
                  cursor: "pointer",
                  ml: 0.5,
                }}
              >
                {expanded ? " see less" : "see more"}
              </Box>
            )}
          </Typography>

          {/* Actions */}
          {isBusy ? (
            <Box sx={{ display: "flex", alignItems: "center", py: 0.5 }}>
              <CircularProgress size={16} />
            </Box>
          ) : (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                size="small"
                onClick={updateApproveStatus}
                sx={{
                  bgcolor: comment.isApproved ? "#dcfce7" : "#16a34a",
                  color: comment.isApproved ? "#16a34a" : "#fff",
                  borderRadius: "6px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "12px",
                  px: 1.5,
                  py: 0.5,
                  minWidth: "unset",
                  "&:hover": { bgcolor: "#15803d", color: "#fff" },
                }}
              >
                {comment.isApproved ? "Unapprove" : "Approve"}
              </Button>

              <Button
                size="small"
                onClick={deleteComment}
                sx={{
                  bgcolor: "#fef2f2",
                  color: "#ef4444",
                  borderRadius: "6px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "12px",
                  px: 1.5,
                  py: 0.5,
                  minWidth: "unset",
                  "&:hover": { bgcolor: "#ef4444", color: "#fff" },
                }}
              >
                Delete
              </Button>

              <Button
                size="small"
                onClick={() => setEditModalOpen(true)}
                sx={{
                  bgcolor: "#f1f5f9",
                  color: "#334155",
                  borderRadius: "6px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "12px",
                  px: 1.5,
                  py: 0.5,
                  minWidth: "unset",
                  "&:hover": { bgcolor: "#334155", color: "#fff" },
                }}
              >
                <EditIcon sx={{ fontSize: "0.9rem", mr: 0.5 }} />
                Edit
              </Button>

              <Button
                size="small"
                onClick={() => handleExport("csv", comment)}
                sx={{
                  bgcolor: "#f1f5f9",
                  color: "#334155",
                  borderRadius: "6px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "12px",
                  px: 1.5,
                  py: 0.5,
                  minWidth: "unset",
                  "&:hover": { bgcolor: "#334155", color: "#fff" },
                }}
              >
                <DownloadIcon sx={{ fontSize: "0.9rem", mr: 0.5 }} />
                CSV
              </Button>

              <Button
                size="small"
                disabled={!comment.authorEmail || isConverted}
                onClick={() => setAddContactModalOpen(true)}
                sx={{
                  bgcolor: "#f1f5f9",
                  color: "#334155",
                  borderRadius: "6px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "12px",
                  px: 1.5,
                  py: 0.5,
                  minWidth: "unset",
                  "&:hover": { bgcolor: "#334155", color: "#fff" },
                  "&.Mui-disabled": {
                    bgcolor: "#f8fafc",
                    color: "#94a3b8",
                  },
                }}
              >
                <AddIcon sx={{ fontSize: "0.9rem", mr: 0.5 }} />
                {isConverted ? "Converted" : "Contact"}
              </Button>
            </Stack>
          )}
        </Box>

        <IconButton
          size="small"
          sx={{ mt: 0.5 }}
          onClick={(e) => setMenuAnchorEl(e.currentTarget)}
        >
          <MoreHorizRounded sx={{ fontSize: 28, color: "text.secondary" }} />
        </IconButton>

        <Menu anchorEl={menuAnchorEl} open={menuOpen} onClose={closeMenu}>
          <MenuItem
            onClick={() => {
              closeMenu();
              updateApproveStatus();
            }}
          >
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {comment.isApproved ? "Unapprove" : "Approve"}
            </ListItemText>
          </MenuItem>

          <MenuItem
            onClick={() => {
              closeMenu();
              setEditModalOpen(true);
            }}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>

          <MenuItem
            onClick={() => {
              closeMenu();
              handleExport("csv", comment);
            }}
          >
            <ListItemIcon>
              <DownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>CSV</ListItemText>
          </MenuItem>

          <MenuItem
            disabled={!comment.authorEmail || isConverted}
            onClick={() => {
              closeMenu();
              setAddContactModalOpen(true);
            }}
          >
            <ListItemIcon>
              <AddIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{isConverted ? "Converted" : "Contact"}</ListItemText>
          </MenuItem>

          <MenuItem
            onClick={() => {
              closeMenu();
              deleteComment();
            }}
            sx={{ color: "#ef4444" }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" sx={{ color: "#ef4444" }} />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        </Menu>
      </Stack>
    </Box>
  );
};